import { Controller } from "@hotwired/stimulus"
import { ArcadeStage } from "../utils/arcade_stage"

// « Plus ou moins » : deux espaces côte à côte, on désigne celui qui porte le plus de
// contrats actifs. Le gagnant reste en piste pour la manche suivante — c'est ce qui donne
// l'impression de monter une échelle plutôt que d'enchaîner des tirages sans rapport.
export default class extends Controller {
  static values = { labels: Object, url: String, scoresUrl: String }

  start() {
    this.stage = new ArcadeStage({
      labels: this.labelsValue,
      scoresUrl: this.scoresUrlValue,
      onRestart: () => this.#play(),
      onClose: () => this.#stop(),
    })
    this.stage.open()
    this.panel = document.createElement("div")
    this.panel.className = "arcade-duel"
    this.stage.board.appendChild(this.panel)
    this.#play()
  }

  #stop() {
    window.clearTimeout(this.timer)
    this.request?.abort()
    this.request = null
    this.panel = null
  }

  #play() {
    this.stage.setScore(0)
    this.#next(null)
  }

  async #next(keepId) {
    this.#renderMessage(this.labelsValue.loading || "…")

    this.request?.abort()
    this.request = new AbortController()

    const url = new URL(this.urlValue, window.location.origin)
    if (keepId) url.searchParams.set("keep_id", keepId)

    // Le `try` ne couvre QUE la requête (cf. la même remarque dans arcade_quiz_controller).
    let round
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.request.signal,
      })
      if (!response.ok) throw new Error(response.statusText)

      round = (await response.json()).round
    } catch (error) {
      if (error.name === "AbortError") return
      this.#renderMessage(this.labelsValue.error || "")
      return
    }

    if (!round) return this.#renderMessage(this.labelsValue.empty || this.labelsValue.error || "")

    this.round = round
    this.#renderRound()
  }

  #renderMessage(text) {
    if (!this.panel) return
    this.panel.innerHTML = ""
    const message = document.createElement("p")
    message.className = "arcade-quiz__message"
    message.textContent = text
    this.panel.appendChild(message)
  }

  #renderRound() {
    if (!this.panel) return

    this.panel.innerHTML = ""
    this.panel.append(this.#card(this.round.left), this.#versus(), this.#card(this.round.right))
  }

  #versus() {
    const node = document.createElement("span")
    node.className = "arcade-duel__versus"
    node.textContent = "VS"
    return node
  }

  #card(entry) {
    const card = document.createElement("button")
    card.type = "button"
    card.className = "arcade-duel__card"
    card.dataset.id = String(entry.id)

    const title = document.createElement("span")
    title.className = "arcade-duel__title"
    title.textContent = entry.title

    const kind = document.createElement("span")
    kind.className = "arcade-duel__kind"
    kind.textContent = entry.kind_label

    const count = document.createElement("span")
    count.className = "arcade-duel__count"
    count.textContent = "?"

    const unit = document.createElement("span")
    unit.className = "arcade-duel__unit"
    unit.textContent = this.labelsValue.contracts || ""

    card.append(title, kind, count, unit)
    card.addEventListener("click", () => this.#pick(entry), { once: true })
    return card
  }

  #pick(entry) {
    const { left, right } = this.round
    const winner = left.contracts >= right.contracts ? left : right
    const correct = entry.id === winner.id

    // On dévoile les deux chiffres dans tous les cas : c'est la moitié du plaisir, et ça
    // rend la défaite lisible.
    this.panel.querySelectorAll(".arcade-duel__card").forEach((card) => {
      card.disabled = true
      const source = Number(card.dataset.id) === left.id ? left : right
      card.querySelector(".arcade-duel__count").textContent = String(source.contracts)
      card.classList.add(source.id === winner.id ? "arcade-duel__card--winner" : "arcade-duel__card--loser")
    })

    if (!correct) {
      this.stage.finish({ score: this.stage.score, message: this.labelsValue.wrong || "" })
      return
    }

    this.stage.setScore(this.stage.score + 1)
    // Le temps de lire les chiffres avant que la manche suivante ne les remplace.
    this.timer = window.setTimeout(() => this.#next(winner.id), 1100)
  }
}
